import React, { useState, useEffect } from 'react';
import DepartmentBanner from "../common/DepartmentBanner";
import TermSelector from "../common/TermSelector";
import FinalExamScheduleService from '../../services/Department/FinalExamScheduleService';
import DepartmentService from "../../services/Department/DepartmentService";
import { FaSearch, FaSpinner, FaSort, FaSortUp, FaSortDown, FaTable, FaTh, FaUserTie, FaBook, FaClock, FaCalendarAlt, FaMapMarkerAlt, FaGraduationCap, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './FinalExamSchedule.module.css';
import Pagination from '../common/Pagination';

const FinalExamSchedule = () => {
    document.title = "Final Exam Schedule";
    const params = new URLSearchParams(window.location.search);


    const [selectedTerm, setSelectedTerm] = useState(0);
    const [selectedTermDetails, setSelectedTermDetails] = useState({});
    const [scheduleData, setScheduleData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [viewMode, setViewMode] = useState('table');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [disabledColumns, setDisabledColumns] = useState([]);
    const itemsPerPage = 10;

    // Define column types
    const COLUMN_TYPES = Object.freeze({
        INSTRUCTOR: 31,
        CLASS: 32,
        CLASS_HOUR: 33,
        DATE_OF_FINAL: 34,
        TIME_OF_FINAL: 35,
        ROOM: 36,
        TYPE_OF_EXAM: 37
    });

    const SORT_KEYS = Object.freeze({
        INSTRUCTOR: 'instructor',
        CLASS: 'class',
        CLASS_HOUR: 'classHour'
    });

    // Check if a column is enabled
    const isColumnEnabled = (columnType) => {
        return !disabledColumns.includes(columnType);
    };

    const handleTermChangeEvent = (termId, selectedTerm) => {
        setSelectedTerm(termId);
        setSelectedTermDetails(selectedTerm);
        setCurrentPage(1); // Reset to first page when term changes
    };

    useEffect(() => {
        if (selectedTerm) {
            fetchSchedule(selectedTerm);
        }
    }, [selectedTerm]);

    useEffect(() => {

            // Get column configuration
            DepartmentService.GetColumnConfig(8)
                .then(response => {
                    const { serviceDisabledItems } = response.data || response;
                    if (serviceDisabledItems && serviceDisabledItems.trim() !== "") {
                        setDisabledColumns(serviceDisabledItems.split(',').map(item => parseInt(item.trim())));
                    } else {
                        setDisabledColumns([]);
                    }
                })
                .catch(error => {
                    console.error('Error fetching column configuration:', error);
                    setDisabledColumns([]);
                });
        }
    , []);

    const fetchSchedule = (termId) => {
        setIsLoading(true);
        FinalExamScheduleService.GetByDepartmentId(termId)
            .then(response => {
                setScheduleData(response.data || []);
                setIsLoading(false);
            })
            .catch(error => {
                setIsLoading(false);
                console.error('Error fetching schedule:', error);
            });
    };

    const filteredSchedule = scheduleData.filter(instructor =>
        instructor.instructor.toLowerCase().includes(searchTerm.toLowerCase()) ||
        instructor.classes.some(classInfo =>
            classInfo.class.toLowerCase().includes(searchTerm.toLowerCase()) ||
            classInfo.classHour.toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredSchedule.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredSchedule.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (columnName) => {
        if (sortConfig.key !== columnName) {
            return <FaSort className="ms-1 text-muted" />;
        }
        return sortConfig.direction === 'asc'
            ? <FaSortUp className="ms-1" />
            : <FaSortDown className="ms-1" />;
    };

    return (
        <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="container-fluid p-0" 
            style={{ backgroundColor: '#f8f9fa' }}
        >
            <div style={{ position: 'relative', zIndex: 1 }}>
            {/* <DepartmentBanner /> */}
                
                <div className="container-custom py-4">
                    {/* Breadcrumb */}
                    <nav aria-label="breadcrumb" className="mb-4">
                        <ol className="breadcrumb bg-transparent p-0">
                            <li className="breadcrumb-item">

                            </li>
                            {/* <li className="breadcrumb-item active" aria-current="page">Final Exam Schedule</li> */}
                        </ol>
                    </nav>

                    {/* Stats Cards */}
                    <div className="row mb-4">
                        <div className="col-md-4">
                            <div className="card border-0 shadow-sm stats-card">
                                <div className="card-body">
                                    <TermSelector onTermChangeEvent={handleTermChangeEvent} />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card border-0 shadow-sm stats-card">
                                <div className="card-body">
                                    <p className="text-muted mb-2 h3">Total Faculty</p>
                                    <p className="mb-0 h3">{scheduleData.length}</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card border-0 shadow-sm stats-card">
                                <div className="card-body">
                                    <p className="text-muted mb-2 h3">Current Term</p>
                                    <p className="mb-0">{selectedTermDetails.termWithYearLast || 'Not Selected'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Header Section */}
                    <div className="card shadow-sm border-0 mb-4">
                        <div className="card-body">
                            <div className="row align-items-center">
                                <div className="col-lg-3">
                                    <p className="mb-0 h3">Final Exam Schedule</p>
                                    <p className="text-muted mb-0">View examination details</p>
                                </div>
                                <div className="col-lg-9">
                                    <div className="d-flex gap-3 flex-wrap">
                                        <div className="flex-grow-1">
                                            <div className="input-group">
                                                <span className="input-group-text bg-white border-end-0">
                                                    <FaSearch className="text-muted" />
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control border-start-0"
                                                    placeholder="Search schedule..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="btn-group">
                                            <button type="button" aria-label="Open Table"
                                                className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setViewMode('table')}
                                            >
                                                <FaTable />
                                            </button>
                                            <button type="button" aria-label="Open Grid"
                                                className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setViewMode('grid')}
                                            >
                                                <FaTh />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content Section */}
                    <AnimatePresence mode="wait">
                        {isLoading ? (
                                          <div className="text-center py-5">
                                          <p className="text-muted">Loading schedule...</p>
                                        </div>
                        
                        ) : viewMode === 'table' ? (
                            <motion.div
                                key="table"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="card shadow-sm border-0"
                            >
                                <div className="card-body p-0">
                                    <div className={styles.tableWrapper}>
                                        <table className="table table-hover mb-0">
                                            <thead>
                                                <tr className="bg-light">
                                                    {isColumnEnabled(COLUMN_TYPES.INSTRUCTOR) && (
                                                        <th className={`${styles.tableCell} ${styles.instructorCell} cursor-pointer`}
                                                            onClick={() => requestSort(SORT_KEYS.INSTRUCTOR)}>
                                                            Instructor {getSortIcon(SORT_KEYS.INSTRUCTOR)}
                                                        </th>
                                                    )}
                                                    {isColumnEnabled(COLUMN_TYPES.CLASS) && (
                                                        <th className={`${styles.tableCell} ${styles.classCell} cursor-pointer`}
                                                            onClick={() => requestSort(SORT_KEYS.CLASS)}>
                                                            Class {getSortIcon(SORT_KEYS.CLASS)}
                                                        </th>
                                                    )}
                                                    {isColumnEnabled(COLUMN_TYPES.CLASS_HOUR) && (
                                                        <th className={`${styles.tableCell} ${styles.classHourCell} cursor-pointer`}
                                                            onClick={() => requestSort(SORT_KEYS.CLASS_HOUR)}>
                                                            Class Hour {getSortIcon(SORT_KEYS.CLASS_HOUR)}
                                                        </th>
                                                    )}
                                                    {isColumnEnabled(COLUMN_TYPES.DATE_OF_FINAL) && (
                                                        <th className={`${styles.tableCell} ${styles.dateCell}`}>Date of Final</th>
                                                    )}
                                                    {isColumnEnabled(COLUMN_TYPES.TIME_OF_FINAL) && (
                                                        <th className={`${styles.tableCell} ${styles.timeCell}`}>Time of Final</th>
                                                    )}
                                                    {isColumnEnabled(COLUMN_TYPES.ROOM) && (
                                                        <th className={`${styles.tableCell} ${styles.roomCell}`}>Room</th>
                                                    )}
                                                    {isColumnEnabled(COLUMN_TYPES.TYPE_OF_EXAM) && (
                                                        <th className={`${styles.tableCell} ${styles.typeCell}`}>Type of Exam</th>
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentItems.length > 0 ? (
                                                    currentItems.map((instructor, index) => (
                                                        instructor.classes.map((classInfo, classIndex) => (
                                                            <motion.tr
                                                                key={`${index}-${classIndex}`}
                                                                initial={{ opacity: 0 }}
                                                                animate={{ opacity: 1 }}
                                                                transition={{ delay: index * 0.05 }}
                                                                className="align-middle"
                                                            >
                                                                {classIndex === 0 && isColumnEnabled(COLUMN_TYPES.INSTRUCTOR) && (
                                                                    <td rowSpan={instructor.classes.length}
                                                                        className={`${styles.tableCell} ${styles.instructorCell}`}>
                                                                        <div className="d-flex align-items-center">
                                                                            <div className="rounded-circle bg-primary bg-opacity-10 p-2 me-2">
                                                                                <FaUserTie className="text-primary" />
                                                                            </div>
                                                                            <span>{instructor.instructor}</span>
                                                                        </div>
                                                                    </td>
                                                                )}
                                                                {isColumnEnabled(COLUMN_TYPES.CLASS) && (
                                                                    <td className={`${styles.tableCell} ${styles.classCell}`}>
                                                                        <FaBook className={styles.icon} />
                                                                        {classInfo.class}
                                                                    </td>
                                                                )}
                                                                {isColumnEnabled(COLUMN_TYPES.CLASS_HOUR) && (
                                                                    <td className={`${styles.tableCell} ${styles.classHourCell}`}>
                                                                        <FaClock className={styles.icon} />
                                                                        {classInfo.classHour}
                                                                    </td>
                                                                )}
                                                                {isColumnEnabled(COLUMN_TYPES.DATE_OF_FINAL) && (
                                                                    <td className={`${styles.tableCell} ${styles.dateCell}`}>
                                                                        <FaCalendarAlt className={styles.icon} />
                                                                        {classInfo.finalDate}
                                                                    </td>
                                                                )}
                                                                {isColumnEnabled(COLUMN_TYPES.TIME_OF_FINAL) && (
                                                                    <td className={`${styles.tableCell} ${styles.timeCell}`}>
                                                                        <FaClock className={styles.icon} />
                                                                        {classInfo.finalTime}
                                                                    </td>
                                                                )}
                                                                {isColumnEnabled(COLUMN_TYPES.ROOM) && (
                                                                    <td className={`${styles.tableCell} ${styles.roomCell}`}>
                                                                        <FaMapMarkerAlt className={styles.icon} />
                                                                        {classInfo.room}
                                                                    </td>
                                                                )}
                                                                {isColumnEnabled(COLUMN_TYPES.TYPE_OF_EXAM) && (
                                                                    <td className={`${styles.tableCell} ${styles.typeCell}`}>
                                                                        <FaGraduationCap className={styles.icon} />
                                                                        {classInfo.typeOfExam}
                                                                    </td>
                                                                )}
                                                            </motion.tr>
                                                        ))
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={Object.values(COLUMN_TYPES).filter(type => isColumnEnabled(type)).length} className="text-center py-5">
                                                            <p className="text-muted mb-0">No exam schedule found</p>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="grid"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="row g-4"
                            >
                                {currentItems.map((instructor, index) => (
                                    <motion.div
                                        key={index}
                                        className="col-md-6 col-lg-4"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                    >
                                        <div className="card border-0 shadow-sm hover-shadow">
                                            <div className={styles.examCard}>
                                                {isColumnEnabled(COLUMN_TYPES.INSTRUCTOR) && (
                                                    <div className="d-flex align-items-center mb-4">
                                                        <div className="rounded-circle bg-primary bg-opacity-10 p-3 me-3">
                                                            <FaUserTie className="text-primary" size={24} />
                                                        </div>
                                                        <h5 className="card-title mb-0">{instructor.instructor}</h5>
                                                    </div>
                                                )}
                                                {instructor.classes.map((classInfo, classIndex) => (
                                                    <div key={classIndex} className={styles.examInfo}>
                                                        {isColumnEnabled(COLUMN_TYPES.CLASS) && (
                                                            <div className="d-flex align-items-center mb-2">
                                                                <FaBook className="text-primary me-2" />
                                                                <strong>{classInfo.class}</strong>
                                                            </div>
                                                        )}
                                                        <div className="ms-4">
                                                            {isColumnEnabled(COLUMN_TYPES.CLASS_HOUR) && (
                                                                <div className="mb-2">
                                                                    <FaClock className={styles.icon} />
                                                                    Class Time: {classInfo.classHour}
                                                                </div>
                                                            )}
                                                            {isColumnEnabled(COLUMN_TYPES.DATE_OF_FINAL) && (
                                                                <div className="mb-2">
                                                                    <FaCalendarAlt className={styles.icon} />
                                                                    Exam Date: {classInfo.finalDate}
                                                                </div>
                                                            )}
                                                            {isColumnEnabled(COLUMN_TYPES.TIME_OF_FINAL) && (
                                                                <div className="mb-2">
                                                                    <FaClock className={styles.icon} />
                                                                    Exam Time: {classInfo.finalTime}
                                                                </div>
                                                            )}
                                                            {isColumnEnabled(COLUMN_TYPES.ROOM) && (
                                                                <div className="mb-2">
                                                                    <FaMapMarkerAlt className={styles.icon} />
                                                                    Room: {classInfo.room}
                                                                </div>
                                                            )}
                                                            {isColumnEnabled(COLUMN_TYPES.TYPE_OF_EXAM) && (
                                                                <div>
                                                                    <FaGraduationCap className={styles.icon} />
                                                                    Type: {classInfo.typeOfExam}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                                {currentItems.length === 0 && (
                                    <div className="col-12 text-center py-5">
                                        <p className="text-muted mb-0">No exam schedule found</p>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Pagination */}
                    <Pagination
                        currentPage={currentPage}
                        totalItems={filteredSchedule.length}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                    />
                </div>
            </div>
        </motion.div>
    );
};

export default FinalExamSchedule;
